import type { Partner } from "@expert/shared-types";
import type { JSX } from "react";

import { EligibilityStatus, SaleProgress, type SaleStatusStyle, StatusStyles } from "../../../../types";
import { DeclinedIcon, EnrolledIcon, IneligibleIcon, PendingIcon, UnknownIcon } from "../Icons";
import KungFuKat from "../Icons/KungFuKat.svg";

interface SalesView {
    badgeText: string | ((partner: Partner) => string);
    badgeStyle: SaleStatusStyle | undefined;
    statusHeader: string;
    statusBody: (planType: "Home" | "Other", hasOtherProducts?: boolean) => string;
    icon?: JSX.Element;
}

const statusMap: Record<string, SalesView | undefined> = {
    [EligibilityStatus.Ineligible]: {
        badgeText: "Not eligible",
        badgeStyle: StatusStyles.Neutral,
        statusHeader: "Not eligible",
        statusBody: (planType, hasOtherProducts) =>
            planType === "Home"
                ? hasOtherProducts
                    ? "They might be eligible for other products—check the Other plans tab."
                    : "They\u2019re not eligible for any products."
                : "They\u2019re not eligible for any additional products.",
        icon: IneligibleIcon,
    },
    [EligibilityStatus.Unknown]: {
        badgeText: "Unknown",
        badgeStyle: StatusStyles.Neutral,
        statusHeader: "Eligibility unknown",
        statusBody: (_planType) => "We couldn\u2019t find the customer\u2019s eligibility.",
        icon: UnknownIcon,
    },
    [EligibilityStatus.Pending]: {
        badgeText: "Pending",
        badgeStyle: StatusStyles.Neutral,
        statusHeader: "Pending enrollment",
        statusBody: (_planType) =>
            "This customer has already been enrolled in Verizon Home Device Protect and should receive a welcome email within 24-48 hours.",
        icon: PendingIcon,
    },
    [EligibilityStatus.Enrolled]: {
        badgeText: "Enrolled",
        badgeStyle: StatusStyles.success,
        statusHeader: "Already enrolled",
        statusBody: (_planType) => "This customer is already enrolled in an available protection plan.",
        icon: EnrolledIcon,
    },
    [SaleProgress.Declined]: {
        badgeText: "Eligible",
        badgeStyle: StatusStyles.success,
        statusHeader: "Customer declined",
        statusBody: (_planType) => "Don\u2019t worry\u2014you\u2019ll get them next time",
        icon: DeclinedIcon,
    },
    [SaleProgress.Failure]: {
        badgeText: "Eligible",
        badgeStyle: StatusStyles.success,
        statusHeader: "Enrollment failed",
        statusBody: (_planType) => `Let the customer know that we couldn\u2019t sign them up at this time`,
        icon: IneligibleIcon,
    },
    [SaleProgress.Success]: {
        badgeText: "Eligible",
        badgeStyle: StatusStyles.success,
        statusHeader: "Nice sale!",
        statusBody: (_planType) => "The customer will get a confirmation email with plan details within 72 hours.",
        icon: <img data-testid="kungfu-kat-animation" src={KungFuKat} alt="" />,
    },
};

export const getSalesViewConfig = (status: EligibilityStatus | SaleProgress) => {
    return (
        statusMap[status] ?? {
            badgeText: "Unknown",
            badgeStyle: StatusStyles.Neutral,
            statusHeader: "Unknown",
            statusBody: (_planType) => "Something went wrong.",
            icon: IneligibleIcon,
        }
    );
};
